import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, sendEmailVerification, createUserWithEmailAndPassword } from "firebase/auth";

// MUI components
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';

// Components
import { ENDPOINT_PATH } from '../config';
import { authFetch } from '../utils/authFetch';
import '../components/Login/login.css';

const Register = () => {
    const navigate = useNavigate();
    
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [goodFirstName, setGoodFirstName] = useState(true);
    const [goodLastName, setGoodLastName] = useState(true);
    const [goodEmail, setGoodEmail] = useState(true);
    const [goodPassword, setGoodPassword] = useState(true);
    const [goodConfirmPassword, setGoodConfirmPassword] = useState(true);
    const [allFieldsValid, setAllFieldsValid] = useState(false);
    const [error, setError] = useState('');

    // Validation helper
    const hasNumbers = (str) => {
        return /\d/.test(str);
    };

    // First name validation
    useEffect(() => {
        if(hasNumbers(firstName)) {
            setGoodFirstName(false);
        } else {
            setGoodFirstName(true);
        }
    }, [firstName]);

    // Last name validation
    useEffect(() => {
        if(hasNumbers(lastName)) {
            setGoodLastName(false);
        } else {
            setGoodLastName(true);
        }
    }, [lastName]);

    // Email validation
    useEffect(() => {
        if(/\S+@\S+\.\S+/.test(email.trim()) || email === '') {
            setGoodEmail(true);
        } else {
            setGoodEmail(false);
        }
    }, [email]);

    // Password validation
    useEffect(() => {
        if(password.length >= 6 || password === '') {
            setGoodPassword(true);
        } else {
            setGoodPassword(false);
        }
    }, [password]);

    // Confirm password validation
    useEffect(() => {
        if(confirmPassword === password || confirmPassword === '') {
            setGoodConfirmPassword(true);
        } else {
            setGoodConfirmPassword(false);
        }
    }, [confirmPassword, password]);

    // Check if all inputs are filled in and valid
    useEffect(() => {
        setAllFieldsValid(
            firstName !== '' && goodFirstName &&
            lastName !== '' && goodLastName &&
            email !== '' && goodEmail &&
            password !== '' && goodPassword &&
            confirmPassword !== '' && goodConfirmPassword
        );
    }, [firstName, goodFirstName, lastName, goodLastName, email, goodEmail, password, goodPassword, confirmPassword, goodConfirmPassword]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const auth = getAuth();

        createUserWithEmailAndPassword(auth, email, password)
        .then(async (userCredential) => {
            console.log("Successfully created user with email and password");

            const user = userCredential.user;

            sendEmailVerification(auth.currentUser)
            .then(async () => {
                console.log("Verification email sent to", user.email);
                console.log('User registered successfully:', user);
                console.log("User verification status: ", user.emailVerified);

                try {
                    // Add user to Users collection in Firestore
                    const registerRes = await fetch(ENDPOINT_PATH + '/api/register', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            uid: user.uid,
                            email: email,
                            firstName: firstName,
                            lastName: lastName
                        }),
                    });
                    if (registerRes.ok) {
                        console.log("Successfully registered user");

                        try {
                            // Create a shopping cart for the user
                            const shoppingCartRes = await authFetch(ENDPOINT_PATH + '/api/create-shopping-cart', {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({
                                    userID: user.uid
                                }),
                            });
                            if (shoppingCartRes.ok) {
                                console.log('Successfully created shopping cart for new user');

                                // Update user in local storage
                                localStorage.setItem('user', JSON.stringify(user));

                                // Navigate to email verification page or show message
                                navigate('/verify-email', { 
                                    state: { 
                                        email: user.email,
                                        from: '/'
                                    } 
                                });
                            }
                            else {
                                console.error('Error creating shopping cart for new user:', shoppingCartRes.statusText);
                                setError('Error creating shopping cart. Please try again.');
                            }
                        } catch (error) {
                            console.error('Error creating shopping cart for new user:', error);
                            setError('Error creating shopping cart. Please try again.');
                        }
                    }
                    else {
                        setError("Error adding user to database, please try again later");
                        console.error('Error registering user:', registerRes.statusText);
                    }
                } catch (error) {
                    setError("Error adding user to database, please try again later");
                    console.error('Error adding user to Firestore:', error);
                }
            })
            .catch((error) => {
                setError("Error sending email verification, please try again later");
                console.error('Error verifying user', error);
            });
        })
        .catch((error) => {
            setError("Error creating user with email and password, please try again later");
            console.error('Error creating user with email and password:', error);
        });
    };

    return (
        <div className="login-page-container">
            <div className="login-page-wrapper">
                <div className="login-page-content">
                    {/* Left side - Image (desktop only) */}
                    <div className="login-page-image" />
                    
                    {/* Right side - Form */}
                    <div className="login-page-form-container">
                        {/* Close button - top right of form */}
                        <button 
                            type="button"
                            className="login-page-close-button"
                            onClick={() => navigate('/')}
                            aria-label="Close"
                        >
                            <CloseIcon />
                        </button>
                        
                        <form className="login-page-form" onSubmit={handleSubmit}>
                            <h2 className="login-page-title">sign up</h2>
                            
                            <div className="login-page-form-fields">
                                {/* First Name and Last Name - side by side on desktop */}
                                <div className="register-page-name-fields">
                                    <div className="register-page-name-field">
                                        <TextField
                                            id="firstName"
                                            label="first name"
                                            variant="outlined"
                                            type="text"
                                            value={firstName}
                                            error={!goodFirstName}
                                            helperText={!goodFirstName ? "a name can't contain numbers" : " "}
                                            onChange={(e) => { setFirstName(e.target.value); }}
                                            fullWidth
                                        />
                                    </div>
                                    
                                    <div className="register-page-name-field">
                                        <TextField
                                            id="lastName"
                                            label="last name"
                                            variant="outlined"
                                            type="text"
                                            value={lastName}
                                            error={!goodLastName}
                                            helperText={!goodLastName ? "a name can't contain numbers" : " "}
                                            onChange={(e) => { setLastName(e.target.value); }}
                                            fullWidth
                                        />
                                    </div>
                                </div>
                                
                                {/* Email */}
                                <div className="login-page-field">
                                    <TextField
                                        id="emailInput"
                                        label="email"
                                        variant="outlined"
                                        type="email"
                                        value={email}
                                        error={!goodEmail}
                                        helperText={!goodEmail ? "invalid email format" : " "}
                                        onChange={(e) => { setEmail(e.target.value); }}
                                        fullWidth
                                    />
                                </div>
                                
                                {/* Password and Confirm Password - side by side on desktop */}
                                <div className="register-page-password-fields">
                                    <div className="register-page-password-field">
                                        <TextField
                                            id="password"
                                            label="password"
                                            variant="outlined"
                                            type="password"
                                            value={password}
                                            error={!goodPassword}
                                            helperText={!goodPassword ? "password must be at least 6 characters long" : " "}
                                            onChange={(e) => { setPassword(e.target.value); }}
                                            fullWidth
                                        />
                                    </div>
                                    
                                    <div className="register-page-password-field">
                                        <TextField
                                            id="confirmPassword"
                                            label="confirm password"
                                            variant="outlined"
                                            type="password"
                                            value={confirmPassword}
                                            error={!goodConfirmPassword}
                                            helperText={!goodConfirmPassword ? "passwords must match" : " "}
                                            onChange={(e) => { setConfirmPassword(e.target.value); }}
                                            fullWidth
                                        />
                                    </div>
                                </div>
                                
                                {/* Error message */}
                                {error && (
                                    <div className="errorMessage login-page-error">
                                        <div>{error}</div>
                                    </div>
                                )}
                                
                                {/* Register button */}
                                <div className="login-page-actions">
                                    <button 
                                        type="submit" 
                                        className={`btn btn-danger btn-lg w-100 ${!allFieldsValid && "disabled"}`}
                                        disabled={!allFieldsValid}
                                    >
                                        register
                                    </button>
                                </div>
                                
                                {/* Link to login */}
                                <div className="login-page-register">
                                    <div className="login-page-register-text">already have an account?</div>
                                    <button 
                                        type="button" 
                                        className="btn button-dark btn-lg w-100" 
                                        onClick={() => navigate('/login')}
                                    >
                                        log in
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Register;

